import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

const EnvironmentManagerPanel = ({
  environments,
  isLoading,
  error,
  createError,
  deleteError,
  isCreating,
  isDeleting,
  onCreateEnvironment,
  onDeleteEnvironment,
  onSelectEnvironment,
  environmentDetail,
  environmentDetailLoading,
  onFetchDetail,
}) => {
  const [newEnvName, setNewEnvName] = useState('');
  const [newEnvDescription, setNewEnvDescription] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [selectedEnvId, setSelectedEnvId] = useState(null);

  const handleCreate = useCallback(async () => {
    if (!newEnvName.trim()) return;
    await onCreateEnvironment(newEnvName.trim(), newEnvDescription.trim());
    setNewEnvName('');
    setNewEnvDescription('');
    setShowCreateForm(false);
  }, [newEnvName, newEnvDescription, onCreateEnvironment]);

  const handleDelete = useCallback(async (envId) => {
    await onDeleteEnvironment(envId);
    setConfirmDeleteId(null);
    if (selectedEnvId === envId) {
      setSelectedEnvId(null);
    }
  }, [onDeleteEnvironment, selectedEnvId]);

  const handleViewDetail = useCallback((env) => {
    if (selectedEnvId === env.id) {
      setSelectedEnvId(null);
    } else {
      setSelectedEnvId(env.id);
      onFetchDetail(env.id);
    }
  }, [selectedEnvId, onFetchDetail]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="environment-manager-panel">
      <div className="env-panel-header">
        <h4 className="env-panel-title">Mock Environments</h4>
        <p className="env-panel-description">
          Create named configuration profiles that override default mock settings.
          Consumers send <code>X-Pago-Mock-Environment: &lt;name&gt;</code> header to activate an environment.
        </p>
      </div>

      {(error || createError || deleteError) && (
        <div className="specs-error-banner">
          {error && <div>Error loading environments: {error}</div>}
          {createError && <div>Error creating environment: {createError}</div>}
          {deleteError && <div>Error deleting environment: {deleteError}</div>}
        </div>
      )}

      {/* Create Environment Form */}
      <div className="env-create-section">
        {showCreateForm ? (
          <div className="env-create-form">
            <div className="env-form-row">
              <input
                type="text"
                className="env-input"
                placeholder="Environment name (e.g., qa-team-1)"
                value={newEnvName}
                onChange={(e) => setNewEnvName(e.target.value)}
                disabled={isCreating}
                pattern="^[a-zA-Z0-9_-]+$"
              />
              <input
                type="text"
                className="env-input env-input-wide"
                placeholder="Description (optional)"
                value={newEnvDescription}
                onChange={(e) => setNewEnvDescription(e.target.value)}
                disabled={isCreating}
              />
            </div>
            <div className="env-form-actions">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleCreate}
                disabled={isCreating || !newEnvName.trim()}
              >
                {isCreating ? 'Creating...' : 'Create'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewEnvName('');
                  setNewEnvDescription('');
                }}
                disabled={isCreating}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setShowCreateForm(true)}
          >
            + New Environment
          </button>
        )}
      </div>

      {/* Environment List */}
      {isLoading ? (
        <div className="specs-loading">Loading environments...</div>
      ) : environments.length === 0 ? (
        <div className="specs-empty">
          No environments configured. All requests use default settings.
        </div>
      ) : (
        <div className="env-list">
          <table className="specs-table env-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {environments.map((env) => (
                <React.Fragment key={env.id}>
                  <tr className={`spec-row ${selectedEnvId === env.id ? 'env-row-selected' : ''}`}>
                    <td className="spec-name">
                      <code>{env.name}</code>
                    </td>
                    <td className="spec-title">{env.description || '-'}</td>
                    <td className="spec-version">{formatDate(env.createdAt)}</td>
                    <td className="spec-actions">
                      {confirmDeleteId === env.id ? (
                        <div className="confirm-delete">
                          <span className="confirm-text">Delete?</span>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(env.id)}
                            disabled={isDeleting}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setConfirmDeleteId(null)}
                            disabled={isDeleting}
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <div className="action-buttons">
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => handleViewDetail(env)}
                            title="View configuration"
                          >
                            {selectedEnvId === env.id ? 'Hide' : 'Details'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => setConfirmDeleteId(env.id)}
                            title="Delete environment"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {selectedEnvId === env.id && (
                    <tr className="env-detail-row">
                      <td colSpan="4">
                        <div className="env-detail-content">
                          {environmentDetailLoading ? (
                            <div className="specs-loading">Loading details...</div>
                          ) : environmentDetail && environmentDetail.id === env.id ? (
                            <div>
                              <div className="env-detail-section">
                                <strong>Header:</strong>{' '}
                                <code>X-Pago-Mock-Environment: {env.name}</code>
                              </div>
                              {environmentDetail.specSettings && environmentDetail.specSettings.length > 0 ? (
                                <div className="env-detail-section">
                                  <strong>Spec Settings ({environmentDetail.specSettings.length}):</strong>
                                  <ul className="env-detail-list">
                                    {environmentDetail.specSettings.map((ss) => (
                                      <li key={ss.id}>
                                        <code>{ss.specName}</code>: mode={ss.proxyMode}
                                        {ss.upstreamEnvironment && `, upstream=${ss.upstreamEnvironment}`}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ) : (
                                <div className="env-detail-section">
                                  <em>No spec settings configured. Use the environment selector on the Specs tab to add overrides.</em>
                                </div>
                              )}
                              {environmentDetail.endpointSettings && environmentDetail.endpointSettings.length > 0 && (
                                <div className="env-detail-section">
                                  <strong>Endpoint Settings ({environmentDetail.endpointSettings.length}):</strong>
                                  <ul className="env-detail-list">
                                    {environmentDetail.endpointSettings.map((es) => (
                                      <li key={es.id}>
                                        <code>{es.method} {es.path}</code> → {es.selectedExample || 'default'}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="specs-empty">No details available</div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

EnvironmentManagerPanel.propTypes = {
  environments: PropTypes.array,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  createError: PropTypes.string,
  deleteError: PropTypes.string,
  isCreating: PropTypes.bool,
  isDeleting: PropTypes.bool,
  onCreateEnvironment: PropTypes.func.isRequired,
  onDeleteEnvironment: PropTypes.func.isRequired,
  onSelectEnvironment: PropTypes.func,
  environmentDetail: PropTypes.object,
  environmentDetailLoading: PropTypes.bool,
  onFetchDetail: PropTypes.func.isRequired,
};

EnvironmentManagerPanel.defaultProps = {
  environments: [],
  isLoading: false,
  error: null,
  createError: null,
  deleteError: null,
  isCreating: false,
  isDeleting: false,
  onSelectEnvironment: null,
  environmentDetail: null,
  environmentDetailLoading: false,
};

export default EnvironmentManagerPanel;
